<template>
	<StepTemplate
		title="Summary"
		:restricted-height="true"
		:isLoading="loadingStep === 3"
		:nextNavigation="{ hidden: true }"
		:prevNavigation="{ hidden: false }"
		:lead="lead"
		v-on="$listeners"
		@prev="clearSummary"
	>
		<div v-if="hasSummary" class="summary type--sans" ref="summary">

			<!-- User contacts -->
			<section class="summary-section">
				<header class="summary-section__header type--b type--s">Your details</header>
				<div class="summary-section__body">
					<FormInput
						name="firstName"
						label="First Name"
						inputClasses="is-white"
						:small="true"
						:compact="true"
						:hasError="$v.firstName.$error"
						:errorMessage="firstNameError"
						v-model="firstName"
					/>
					<FormInput
						name="lastName"
						label="Last Name"
						inputClasses="is-white"
						:small="true"
						:compact="true"
						:hasError="$v.lastName.$error"
						:errorMessage="lastNameError"
						v-model="lastName"
					/>
					<FormInput
						name="email"
						type="email"
						label="Email"
						inputClasses="is-white"
						:small="true"
						:compact="true"
						:hasError="$v.email.$error"
						:errorMessage="emailError"
						v-model="email"
					/>
					<FormInput
						name="phone"
						type="tel"
						label="Phone"
						inputClasses="is-white"
						:small="true"
						:compact="true"
						:hasError="$v.phone.$error"
						:errorMessage="phoneError"
						v-model="phone"
					/>
				</div>
			</section>

			<!-- Outbound -->
			<section class="summary-section">
				<header class="summary-section__header type--b type--s">Outbound, {{ formattedDate }}</header>
				<div class="summary-section__body">
					<ul class="journey">
						<li class="summary-step journey__stop is-helipad">
							<div class="journey__stop-marker">
								<FlytJourneyLocationIcon/>
							</div>
							<div class="journey__stop-content">
								<p class="type--s">{{steps[0].depart_time}}</p>
								<p class="type--b">{{steps[0].location.name}}</p>
								<p class="type--s">{{steps[0].location.display_name}}</p>
							</div>
						</li>
						<li class="summary-step journey__stop is-helipad">
							<div class="journey__stop-marker">
								<FlytJourneyLocationIcon/>
							</div>
							<div class="journey__stop-content">
								<p class="type--s">{{steps[1].arrive_time}}</p>
								<p class="type--b">{{steps[1].location.name}}</p>
								<p class="type--s">{{steps[1].location.display_name}}</p>
							</div>
						</li>
					</ul>
				</div>
			</section>

			<!-- Return -->
			<section v-if="hasReturn" class="summary-section">
				<header class="summary-section__header type--b type--s">Return, {{ formattedReturnDate }}</header>
				<div class="summary-section__body">
					<ul class="journey">
						<li class="summary-step journey__stop is-helipad">
							<div class="journey__stop-marker">
								<FlytJourneyLocationIcon/>
							</div>
							<div class="journey__stop-content">
								<p class="type--s">{{returnSteps[0].depart_time}}</p>
								<p class="type--b">{{returnSteps[0].location.name}}</p>
								<p class="type--s">{{returnSteps[0].location.display_name}}</p>
							</div>
						</li>
						<li class="summary-step journey__stop is-helipad">
							<div class="journey__stop-marker">
								<FlytJourneyLocationIcon/>
							</div>
							<div class="journey__stop-content">
								<p class="type--s">{{returnSteps[1].arrive_time}}</p>
								<p class="type--b">{{returnSteps[1].location.name}}</p>
								<p class="type--s">{{returnSteps[1].location.display_name}}</p>
							</div>
						</li>
					</ul>
				</div>
			</section>

			<!-- Aircraft -->
			<section class="summary-section">
				<div class="summary-helicopter">
					<div class="summary-helicopter__image">
						<img :src="`${aircraft.aircraft_model.thumbnail_image}`" alt />
					</div>
					<div class="summary-helicopter__text type--s">
						<span class="type--b">Your {{ aircraftCategory }}</span>
						<span class>{{ aircraft.aircraft_model.model_no }}</span>
					</div>
				</div>
			</section>

			<!-- Cost -->
			<section class="summary-section">
				<div class="summary-cost">
					<span>Estimated Net Cost</span>
					<span class="type--b">{{ totalCost }}</span>
				</div>
			</section>

			<!-- Message -->
			<section class="summary-section">
				<header class="summary-section__header type--b type--s">Message</header>
				<div class="summary-section__body">
					<p class="type--serif">Leave us a message with any further requests or questions.</p>
					<textarea v-model="message" name="message" id="message" class="summary-message"></textarea>
				</div>
			</section>
		</div>

		<template #footer>
			<div class="form-group">
				<button
					class="button is-primary is-wide type--serif type--m"
					@mouseover="$v.$touch()"
					@mouseout="$v.$reset()"
					@click="handleSubmit"
				>Get Final Quote</button>
			</div>
		</template>

	</StepTemplate>
</template>

<script>
import StepTemplate from "./StepTemplate"
import FormInput from "@/components/formfields/FormInput"
import { mapGetters, mapActions } from "vuex"
import { required, email, numeric } from "vuelidate/lib/validators"
import { phoneLength } from "@/utils/validators"
import FlytJourneyLocationIcon from "@/components/FlytJourneyLocationIcon"

export default {
	data() {
		return {
			firstName: "",
			lastName: "",
			email: '',
			phone: '',
			lead: "We will send you the exact details including your flight time, aircraft, and total cost.",
			successMessage:
				"Thanks for your enquiry. We’ll finalise all the details and send you an email with confirmation of your flight time, aircraft and extras.",
			message: "",
			imageBaseUrl: `${process.env.VUE_APP_API_URL}images/uploads/helicoptertypes/`,
		}
	},

	name: "SummaryStep",

	components: { StepTemplate, FormInput, FlytJourneyLocationIcon },

	computed: {
		...mapGetters("bookingForm", [
			"loadingStep",
			"aircraftCategory"
		]),
		...mapGetters("summary", [
			"hasSummary",
			"hasReturn",
			"steps",
			"formattedDate",
			"formattedReturnDate",
			"returnSteps",
			"aircraft",
			"totalCost"
		]),

		firstNameError() {
			let errorMessage = "Invalid input"
			if (!this.$v.firstName.required)
				errorMessage = "Please enter your first name"

			return errorMessage
		},

		lastNameError() {
			let errorMessage = "Invalid input"
			if (!this.$v.lastName.required)
				errorMessage = "Please enter your last name"

			return errorMessage
		},

		emailError() {
			let errorMessage = "Invalid input"
			if (!this.$v.email.email)
				errorMessage = "Must be a valid email address"
			if (!this.$v.email.required)
				errorMessage = "Email address is required"

			return errorMessage
		},

		phoneError() {
			let errorMessage = "Invalid input"
			if (!this.$v.phone.phoneLength)
				errorMessage = this.$v.phone.$params.phoneLength.message
			if (!this.$v.phone.required)
				errorMessage = "Phone number is required"

			return errorMessage
		},

		payload() {
			return {
				helicopter: {
					helicopter_id: this.aircraft.id
				},
				guest: {
					first_name: this.firstName,
					last_name: this.lastName,
					email: this.email,
					phone: this.phone
				},
				message: this.message,
			}
		},
	},

	validations: {
		firstName: {
			required
		},
		lastName: {
			required
		},
		email: {
			required,
			email,
		},
		phone: {
			numeric,
			phoneLength,
		},
	},

	methods: {
		...mapActions("summary", [
			"submitSummary",
			"clearSummary"
		]),

		/**
		 * Scroll form to first input with error
		 */
		scrollToFirstError() {
			let firstError = this.$refs.summary.querySelector('.has-error')

			if (!firstError)
				return

			firstError.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'start'
			})
		},

		/**
		 * Check for errors, if none found submit the form.
		 * If submit successful, move onto confirmation.
		 */
		async handleSubmit() {
            this.$v.$touch()

			if (this.$v.$invalid) {
				this.scrollToFirstError()
				return
			}

			this.submitSummary(this.payload)
			this.$emit("next")
		},
	},
}
</script>

<style lang="scss" scoped>
.summary-section {
	background-color: $colour-pencil--lightest;
	border-radius: 0.25rem;
	overflow: hidden;
	&:not(:last-child) {
		margin-bottom: 1.25rem;
	}
}

.summary-section__header {
	border-bottom: 1px dotted $colour-pencil;
	margin: 0.75rem 0.75rem 0;
	padding: 0 0 0.5rem;
}

.summary-section__body {
	padding: 0.75rem;
}

.summary-helicopter {
	display: flex;
	flex-direction: row;
}

.summary-helicopter__image {
	flex: 0 0 50%;
	img {
		display: block;
		height: auto;
		width: 100%;
	}
}

.summary-helicopter__text {
	display: flex;
	flex-direction: column;
	flex: 0 0 50%;
	justify-content: center;
	padding: 0.75rem;
}

.summary-cost {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	padding: 0.75rem 0.75rem 0.625rem;
}

.summary-message {
	background: #fff;
	border: none;
	display: block;
	margin-top: 1rem;
	max-height: 15rem;
	min-height: 5rem;
	resize: vertical;
}

.summary-success {
	color: $colour-teal;
	margin: 0.5rem 0;
}
</style>
